import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ExternalLink, Link2, Link2Off, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { XeroStatusBadge } from "./XeroContactsCard";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "never");

export default function XeroContactLink({ userId, details, onChanged }) {
  const [busy, setBusy] = useState("");
  const [linking, setLinking] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!linking || q.trim().length < 2) { setResults(null); return; }
    let stale = false;
    const t = setTimeout(() => {
      api.get("/admin/xero/contacts/search", { params: { q } }).then((r) => { if (!stale) setResults(r.data); })
        .catch((e) => { if (stale) return; toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Search failed"); setResults([]); });
    }, 350);
    return () => { stale = true; clearTimeout(t); };
  }, [q, linking]);

  if (!details.xero_eligible) return null;

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { const { data } = await fn(); toast.success(typeof okMsg === "function" ? okMsg(data) : okMsg); setLinking(false); setQ(""); onChanged?.(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Xero request failed"); onChanged?.(); }
    finally { setBusy(""); }
  };
  const sync = () => run("sync", () => api.post(`/admin/xero/contacts/${userId}/sync`), (d) => (d.changes?.length ? `Synced — from Xero: ${d.changes.join("; ")}` : `Xero contact ${d.action}`));
  const link = (c) => run("link", () => api.post(`/admin/xero/contacts/${userId}/link`, { contact_id: c.contact_id }), `Linked to "${c.name}" in Xero`);
  const unlink = () => run("unlink", () => api.post(`/admin/xero/contacts/${userId}/unlink`), "Unlinked from Xero");

  return (
    <div className="mt-4 rounded-md border border-dashed p-3" data-testid="xero-contact-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Xero contact <XeroStatusBadge row={details} /></div>
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" variant="outline" onClick={sync} disabled={!!busy} data-testid="xero-sync-now" className="h-7 gap-1 text-xs">{busy === "sync" ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Sync now</Button>
          <Button size="sm" variant="outline" onClick={() => setLinking((v) => !v)} disabled={!!busy} data-testid="xero-link-toggle" className="h-7 gap-1 text-xs"><Link2 className="h-3 w-3" /> {details.xero_contact_id ? "Re-link" : "Link existing"}</Button>
          {details.xero_contact_id && <Button size="sm" variant="ghost" onClick={unlink} disabled={!!busy} data-testid="xero-unlink" className="h-7 gap-1 text-xs text-red-700 hover:text-red-800"><Link2Off className="h-3 w-3" /> Unlink</Button>}
        </div>
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground" data-testid="xero-contact-status-text">
        {details.xero_contact_id ? <>Linked to <b>{details.xero_contact_name || "Xero contact"}</b> · last synced {fmt(details.xero_synced_at)} {details.xero_url && <a href={details.xero_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-kilted-green underline" data-testid="xero-open-contact">Open <ExternalLink className="h-3 w-3" /></a>}</>
          : "Not yet in Xero — Sync now creates the contact, or link one that already exists."}
      </p>
      {details.xero_sync_error && <p className="mt-1 text-xs text-red-700" data-testid="xero-contact-error">{details.xero_sync_error}</p>}
      {linking && (
        <div className="mt-2 space-y-1.5">
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Xero contacts by name or email…" data-testid="xero-link-search" className="h-8 text-sm" />
          {results && (results.length === 0 ? <p className="text-xs text-muted-foreground">No Xero contacts match.</p> : (
            <ul className="max-h-40 space-y-1 overflow-y-auto" data-testid="xero-link-results">
              {results.map((c) => (
                <li key={c.contact_id} className="flex items-center justify-between gap-2 rounded border bg-secondary/40 px-2 py-1 text-xs">
                  <span><b>{c.name}</b>{c.email ? ` · ${c.email}` : ""}</span>
                  <Button size="sm" onClick={() => link(c)} disabled={!!busy} data-testid={`xero-link-pick-${c.contact_id}`} className="h-6 bg-kilted-green px-2 text-xs hover:bg-kilted-greenLight">Link</Button>
                </li>
              ))}
            </ul>
          ))}
        </div>
      )}
    </div>
  );
}
